/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

'use client';

import React, { useState, useEffect } from 'react';
import { Linking, View } from 'react-native';
import TextView from '@airasia-phoenix/react-native/components/TextView';
import IconView from '@airasia-phoenix/react-native/components/IconView';
import Close from '@move-travel/react-native-phoenix-icons/Close';
import { pd } from '@airasia-phoenix/react-native/tokens';
import { getTextStyle } from '@airasia-phoenix/react-native/utils/utils';
import styles from './styles';
import type { CookieBannerProps } from './types';
import {
  LINK_TEXT,
  COOKIE_NAME,
  COOKIE_VALUE,
  COOKIE_EXPIRY_DAYS,
  defaultCookieDisclaimer,
} from './constants';
import { getCookie, setCookie } from '../../utils/cookies';

const CookieBanner = ({
  cookieDisclaimer = defaultCookieDisclaimer,
  serverCookieValueForCookieBanner,
  hydrationSafe = false,
}: CookieBannerProps) => {
  const {
    text,
    privacyStatementLink,
    privacyStatementText = defaultCookieDisclaimer.privacyStatementText,
  } = cookieDisclaimer;

  const [visible, setVisible] = useState(() => {
    // Opted in: the FIRST client render must match the server markup byte for
    // byte, so it derives ONLY from the prop the server rendered with — never
    // from document.cookie. Reading the live cookie here made the first client
    // render diverge from any HTML produced under a different cookie state
    // (e.g. an edge-cached page from a consent-accepted visitor served to an
    // incognito visitor): the client inserted a banner the HTML lacked — a
    // structural hydration mismatch (React #418, args[]=HTML) that tears down
    // the whole tree ("removeChild" error → blank page).
    if (hydrationSafe) {
      return serverCookieValueForCookieBanner !== undefined
        ? serverCookieValueForCookieBanner !== COOKIE_VALUE
        : false;
    }

    // Legacy behaviour, kept for LOBs that have not opted in.
    const isBrowser = typeof window !== 'undefined';
    const hasServerCookieValue = serverCookieValueForCookieBanner !== undefined;
    return isBrowser
      ? getCookie(COOKIE_NAME) !== COOKIE_VALUE
      : hasServerCookieValue &&
          serverCookieValueForCookieBanner !== COOKIE_VALUE;
  });

  useEffect(() => {
    const accepted = getCookie(COOKIE_NAME) === COOKIE_VALUE;

    // Opted in: reconcile BOTH directions, so a stale cached page also hides
    // the banner for visitors who already accepted. The legacy path can only
    // reveal it, never hide it.
    if (hydrationSafe) {
      setVisible(!accepted);
    } else if (!accepted) {
      setVisible(true);
    }
  }, [hydrationSafe]);

  const handleDismiss = () => {
    setCookie(COOKIE_NAME, COOKIE_VALUE, { days: COOKIE_EXPIRY_DAYS });
    setVisible(false);
  };

  if (!visible) return null;

  const linkText = LINK_TEXT;
  const processedText = text.replace(`${linkText}`, privacyStatementText);

  return (
    <View style={styles.container}>
      <View style={styles.content}>
        <View style={styles.mainText}>
          <TextView
            text={processedText}
            style={[
              styles.mainText,
              getTextStyle(
                pd.sys.typescale.caption_text,
                pd.sys.colour.content_primary,
              ),
            ]}
            spannableTextsProps={[
              {
                spannable: privacyStatementText,
                highlightColor: pd.sys.colour.interactive_link.toString(),
                style: getTextStyle(
                  pd.sys.typescale.caption_text,
                  pd.sys.colour.interactive_link,
                ),
                onPress: privacyStatementLink
                  ? () => {
                      Linking.openURL(privacyStatementLink);
                    }
                  : undefined,
              },
            ]}
          />
        </View>
        <IconView
          width={20}
          height={20}
          tint={pd.sys.colour.content_primary.toString()}
          onPress={handleDismiss}
        >
          <Close />
        </IconView>
      </View>
    </View>
  );
};

export default CookieBanner;
